/* eslint-disable  */
import { debounce } from "lodash";
import React, { useEffect, useState } from "react";
import { BsPrinter } from "react-icons/bs";
import { FiSearch } from "react-icons/fi";
import { AiOutlineFilePdf } from "react-icons/ai";
import ReactPaginate from "react-paginate";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import { bindActionCreators } from "redux";
import { userDataActionCreators } from "../../../service/action-creators";
import { GetUserProfile } from "../../../service/action-creators/authAction";
import { GetNotifications } from "../../../service/action-creators/notificationAction";
import { GetCartProducts } from "../../../service/action-creators/userDataAction";
import axiosInstance from "../../../service/axios/axios";
import Loader from "../../Loader";
import RenderHome from "../home/RenderHome";
import {
  downloadFundStatement,
  isoDate,
  printFundStatement,
} from "../../../helpers/fundStatementPdf";
import "./Fund.css";

// The funds list endpoint is paginated 15 at a time for the table; the
// statement needs the whole history, and a member's is comfortably under this.
const ALL_ROWS = 2000;

// The page is styled inline, the way the newer member pages are, because the
// Tailwind stylesheet in src/dist is a generated file that is only rebuilt by
// the `build-css` watcher — new utility classes would silently not exist.
const GREEN = "#2d6a4f";
const GREEN_DARK = "#1b4332";
const INK = "#1a3c2a";

const cedi = (n) =>
  Number(n || 0).toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const card = {
  background: "#fff",
  borderRadius: 14,
  border: "1px solid #e8ece9",
  boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
};

function StatTile({ label, value, hint, accent }) {
  return (
    <div style={{ ...card, padding: "16px 18px", flex: "1 1 180px", minWidth: 160 }}>
      <p style={{ margin: 0, fontSize: 12, color: "#78857e", fontWeight: 600, letterSpacing: 0.3 }}>
        {label}
      </p>
      <p
        style={{
          margin: "6px 0 0",
          fontSize: 22,
          fontWeight: 800,
          color: accent || INK,
          lineHeight: 1.2,
        }}
      >
        {value}
      </p>
      {hint ? (
        <p style={{ margin: "4px 0 0", fontSize: 11.5, color: "#98a29c" }}>{hint}</p>
      ) : null}
    </div>
  );
}

function ActionButton({ icon: Icon, label, onClick, disabled, tone }) {
  const solid = tone === "solid";
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "9px 14px",
        borderRadius: 10,
        fontSize: 13,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        border: solid ? "1px solid transparent" : "1px solid #dde3df",
        background: solid ? GREEN : "#fff",
        color: solid ? "#fff" : "#3c4a43",
        whiteSpace: "nowrap",
      }}
    >
      <Icon size={16} />
      {label}
    </button>
  );
}

function Fund() {
  const dispatch = useDispatch();
  const { GetFunds, GetFundsOpeningBalance, GetTotalFunds } =
    bindActionCreators(userDataActionCreators, dispatch);

  const user = useSelector((state) => state.user);
  const profile = useSelector((state) => state?.auth?.user);
  const [busyAction, setBusyAction] = useState(null); // "print" | "download"

  const { funds, paginate, isLoading, totalFunds, fundsOpeningBalance } = user;

  const [pageCount, setPageCount] = useState(0);

  useEffect(() => {
    setPageCount(Number(paginate?.total) || 0);
  }, [funds, paginate]);

  // Invoke when user click to request another page.
  const handlePageClick = (event) => {
    GetFunds(true, event.selected + 1, 15);
  };

  // Excel export, parked at the members' request — the statement covers both
  // printing and downloading now.
  // const downloadExcelData = async () => {
  //   dispatch(DownloadMemberFunds(false, 1, 15));
  // };

  /**
   * Builds the official watermarked statement — the same document an officer
   * downloads from the admin app, so the two never differ — and either saves it
   * or sends it to the printer.
   *
   * The rows are re-fetched rather than taken from the table: the table holds
   * one 15-row page, and only the rows matching whatever is in the search box.
   */
  const handleStatement = async (action) => {
    setBusyAction(action);
    try {
      const response = await axiosInstance.get(
        `/funds/user?isPaginated=true&page=1&size=${ALL_ROWS}`
      );
      const rows = response?.data?.docs ?? [];
      const totalItems = Number(response?.data?.totalItems);

      if (!rows.length && !opening) {
        toast.info("You have no contributions to put on a statement yet");
        return;
      }

      // A page is the whole history for any real member, but if it ever fills
      // up say so rather than handing over a short statement that looks whole.
      if (rows.length >= ALL_ROWS && totalItems > rows.length) {
        toast.warn(
          `Only the first ${rows.length} of ${totalItems} records were loaded — this statement may be incomplete.`
        );
      }

      const statement = { member: profile, rows, openingBalance: opening };
      if (action === "print") {
        await printFundStatement(statement);
      } else {
        await downloadFundStatement(statement);
      }
    } catch (e) {
      console.error(e);
      toast.error(
        e?.response?.data?.message ?? "Could not build your statement. Try again."
      );
    } finally {
      setBusyAction(null);
    }
  };

  useEffect(() => {
    GetFunds(true, 1, 15);
    GetFundsOpeningBalance();
    GetTotalFunds();

    dispatch(GetUserProfile());
    dispatch(GetNotifications());
    dispatch(GetCartProducts());
  }, []);

  const handleSearch = debounce((e) => {
    if (e.target.value) {
      GetFunds(true, 1, 15, `${e.target.value}`);
    } else {
      GetFunds(true, 1, 15);
    }
  }, 800);

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 820);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 820);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const columns = [
    { key: "transaction_type", label: "Transaction Type" },
    { key: "period", label: "Period" },
    { key: "original_amount", label: "Original Amount", numeric: true },
    { key: "deduction", label: "Deduction", numeric: true },
    { key: "balance", label: "Balance", numeric: true },
  ];

  const cellValue = (row, column) =>
    column.numeric ? `¢${cedi(row?.[column.key])}` : row?.[column.key] || "—";

  // The opening balance is a brought-forward figure, not a deduction of its
  // own, so it leads the list as its own highlighted line — the same place the
  // statement PDF gives it. Guarded on the figure rather than the record:
  // /funds/opening-balance answers for every member, with nothing in it for
  // those who never had one.
  const opening = Number(fundsOpeningBalance?.deduction) > 0 ? fundsOpeningBalance : null;

  // GET /funds/member/total sums the fund rows only, so the brought-forward
  // figure is added back here — otherwise the page shows an opening balance
  // above a total that excludes it, and the total wouldn't match the statement
  // PDF. (This is what the original getMemberTotalFund in the API did.)
  const contributionsTotal = Number(totalFunds || 0) + Number(opening?.deduction || 0);

  const openingRow = opening
    ? {
      transaction_type: "Opening Balance",
      period: "Start - Nov 22",
      original_amount: opening?.original_amount,
      deduction: opening?.deduction,
      balance: opening?.balance,
    }
    : null;

  return (
    <div className="FundPage">
      <RenderHome>
        {isLoading ? <Loader /> : ""}

        <div style={{ maxWidth: 1100, margin: "0 auto", paddingBottom: 32 }}>
          {/* Header */}
          <div
            style={{
              ...card,
              background: `linear-gradient(135deg, ${GREEN} 0%, ${GREEN_DARK} 100%)`,
              border: "none",
              color: "#fff",
              padding: "22px 24px",
              marginBottom: 16,
            }}
          >
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>My Contributions</h2>
            <p style={{ margin: "6px 0 0", fontSize: 13, opacity: 0.85 }}>
              {profile?.name ? `${profile.name} · ` : ""}
              {profile?.staff_id ? `Staff ID ${profile.staff_id} · ` : ""}
              As at {isoDate()}
            </p>
          </div>

          {/* Summary */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
            <StatTile
              label="TOTAL CONTRIBUTIONS"
              value={`¢${cedi(contributionsTotal)}`}
              hint="All deductions to date"
              accent={GREEN}
            />
            {opening && (
              <StatTile
                label="OPENING BALANCE"
                value={`¢${cedi(opening?.deduction)}`}
                hint="Brought forward · Start - Nov 22"
              />
            )}
            <StatTile
              label="RECORDS"
              value={paginate?.totalItems ?? funds?.length ?? 0}
              hint={pageCount > 1 ? `Across ${pageCount} pages` : "On this page"}
            />
          </div>

          {/* Toolbar */}
          <div
            style={{
              ...card,
              padding: 14,
              marginBottom: 16,
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div style={{ position: "relative", flex: "1 1 260px", minWidth: 200 }}>
              <FiSearch
                size={16}
                style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#9aa49e",
                }}
              />
              <input
                type="search"
                name="searchProduct"
                placeholder="Search by period e.g. JAN22"
                onChange={handleSearch}
                style={{
                  width: "100%",
                  padding: "10px 12px 10px 36px",
                  borderRadius: 10,
                  border: "1px solid #dde3df",
                  outline: "none",
                  fontSize: 13,
                  background: "#fbfcfb",
                }}
              />
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {/* Print sends the official statement to the printer, not a
                  screenshot of this page. */}
              <ActionButton
                icon={BsPrinter}
                label={busyAction === "print" ? "Preparing…" : "Print Statement"}
                onClick={() => handleStatement("print")}
                disabled={!!busyAction}
              />
              {/* Excel export parked — see downloadExcelData above.
              <ActionButton icon={SiMicrosoftexcel} label="Excel" onClick={downloadExcelData} /> */}
              <ActionButton
                icon={AiOutlineFilePdf}
                tone="solid"
                label={busyAction === "download" ? "Preparing…" : "Download Statement"}
                onClick={() => handleStatement("download")}
                disabled={!!busyAction}
              />
            </div>
          </div>

          {/* Records */}
          <div style={{ ...card, overflow: "hidden" }}>
            <div
              style={{
                padding: "14px 18px",
                borderBottom: "1px solid #eef1ef",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: INK }}>
                Fund Contributions
              </h3>
              <span style={{ fontSize: 12, color: "#8b968f" }}>
                Total <strong style={{ color: GREEN }}>¢{cedi(contributionsTotal)}</strong>
              </span>
            </div>

            {isMobile ? (
              /* ── Mobile: card list ── */
              <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
                {[openingRow, ...(funds || [])].filter(Boolean).map((row, index) => (
                  <div
                    key={index}
                    style={{
                      border: "1px solid #e8ece9",
                      borderRadius: 10,
                      padding: 12,
                      background: row === openingRow ? "#f2f8f4" : "#fbfcfb",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 8,
                      }}
                    >
                      <span style={{ fontWeight: 700, fontSize: 13, color: INK }}>
                        {row?.transaction_type || "—"}
                      </span>
                      <span style={{ fontWeight: 800, fontSize: 14, color: GREEN }}>
                        ¢{cedi(row?.deduction)}
                      </span>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, minmax(0,1fr))",
                        gap: 6,
                        fontSize: 11.5,
                      }}
                    >
                      {[
                        ["Period", row?.period || "—"],
                        ["Original", `¢${cedi(row?.original_amount)}`],
                        ["Balance", `¢${cedi(row?.balance)}`],
                      ].map(([label, value]) => (
                        <div key={label}>
                          <p style={{ margin: 0, color: "#98a29c" }}>{label}</p>
                          <p style={{ margin: 0, color: "#3c4a43", fontWeight: 600 }}>{value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {!funds?.length && !openingRow && (
                  <p style={{ textAlign: "center", padding: 30, color: "#98a29c", fontSize: 13 }}>
                    No contributions recorded yet.
                  </p>
                )}
              </div>
            ) : (
              /* ── Desktop: table ── */
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#f7f9f8" }}>
                      {columns.map((column) => (
                        <th
                          key={column.key}
                          style={{
                            padding: "11px 18px",
                            textAlign: column.numeric ? "right" : "left",
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: 0.4,
                            textTransform: "uppercase",
                            color: "#78857e",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {column.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {openingRow && (
                      <tr style={{ background: "#f2f8f4" }}>
                        {columns.map((column) => (
                          <td
                            key={column.key}
                            style={{
                              padding: "11px 18px",
                              fontSize: 13,
                              fontWeight: 600,
                              color: GREEN_DARK,
                              textAlign: column.numeric ? "right" : "left",
                            }}
                          >
                            {cellValue(openingRow, column)}
                          </td>
                        ))}
                      </tr>
                    )}

                    {funds?.map((fund, index) => (
                      <tr key={index} style={{ borderTop: "1px solid #f0f3f1" }}>
                        {columns.map((column) => (
                          <td
                            key={column.key}
                            style={{
                              padding: "11px 18px",
                              fontSize: 13,
                              color: "#3c4a43",
                              textAlign: column.numeric ? "right" : "left",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {cellValue(fund, column)}
                          </td>
                        ))}
                      </tr>
                    ))}

                    {!funds?.length && !openingRow && (
                      <tr>
                        <td
                          colSpan={columns.length}
                          style={{
                            padding: 40,
                            textAlign: "center",
                            color: "#98a29c",
                            fontSize: 13,
                          }}
                        >
                          No contributions recorded yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: "2px solid #e2e8e4", background: "#f7f9f8" }}>
                      <td
                        colSpan={columns.length - 2}
                        style={{ padding: "13px 18px", fontSize: 13, fontWeight: 800, color: INK }}
                      >
                        TOTAL
                      </td>
                      <td
                        style={{
                          padding: "13px 18px",
                          textAlign: "right",
                          fontSize: 14,
                          fontWeight: 800,
                          color: GREEN,
                        }}
                      >
                        ¢{cedi(contributionsTotal)}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {funds?.length > 0 && pageCount > 1 && (
            <ReactPaginate
              breakLabel="..."
              nextLabel=">"
              onPageChange={handlePageClick}
              pageRangeDisplayed={5}
              pageCount={pageCount}
              previousLabel="<"
              renderOnZeroPageCount={null}
              containerClassName="pagination"
              pageLinkClassName="page-num"
              previousLinkClassName="page-num"
              nextLinkClassName="page-num"
              activeLinkClassName="active"
            />
          )}
        </div>

        <ToastContainer autoClose={3000} />
      </RenderHome>
    </div>
  );
}

export default Fund;
